import { balance, type Balance } from './balance';
import type { Rng } from './rng';
import { stageMult, statsAtLevel } from './stats';
import { effectiveness, physicalTypeMult } from './typechart';
import type {
  AttackEffect,
  AttackUse,
  Combatant,
  MatchupInput,
  MatchupKind,
  MatchupResult,
  Move,
  StatName,
} from './types';

export type BattleStat = Exclude<StatName, 'hp'>;
/** The stats one side of a battle reads: one, or two averaged. */
export type BattleStats = readonly [BattleStat] | readonly [BattleStat, BattleStat];
export type BattleMap = Record<MatchupKind, { offense: BattleStats; defense: BattleStats }>;

const MATCHUP_KINDS: readonly MatchupKind[] = [
  'block',
  'coverage',
  'tackle',
  'contested',
  'throw',
  'kick',
];
const BATTLE_STATS: readonly string[] = ['atk', 'def', 'spa', 'spd', 'spe'];

/** balance.json `battles`, checked and typed, built once per balance. */
const BATTLE_MAPS = new WeakMap<Balance, BattleMap>();
export function battleMap(b: Balance): BattleMap {
  let m = BATTLE_MAPS.get(b);
  if (!m) {
    const raw = b.battles as unknown as Record<
      string,
      { offense?: readonly string[]; defense?: readonly string[] } | undefined
    >;
    const side = (kind: string, s: 'offense' | 'defense'): BattleStats => {
      const list = raw[kind]?.[s];
      if (
        !list ||
        list.length < 1 ||
        list.length > 2 ||
        !list.every((x) => BATTLE_STATS.includes(x))
      ) {
        throw new Error(`balance.json battles.${kind}.${s} must list one or two non-HP stats`);
      }
      return list as BattleStats;
    };
    m = Object.fromEntries(
      MATCHUP_KINDS.map((k) => [k, { offense: side(k, 'offense'), defense: side(k, 'defense') }]),
    ) as BattleMap;
    BATTLE_MAPS.set(b, m);
  }
  return m;
}

/**
 * One side's strength from its stats: one as it plays, two averaged, to balance.json
 * `strength.exponent` (1 leaves it as it is).
 */
function read(c: Combatant, stats: BattleStats, b: Balance): number {
  const s =
    stats.length === 1
      ? effectiveStat(c, stats[0], b)
      : (effectiveStat(c, stats[0], b) + effectiveStat(c, stats[1], b)) / 2;
  const p = b.strength.exponent;
  return p === 1 ? s : s ** p;
}

/**
 * Which stats each side brings to each kind of matchup: balance.json `battles` (slice 10g), read
 * at call time so a balance copy's map is the one played. See docs/design-plan.md, "The matchups".
 */
export const STRENGTH: Record<
  MatchupKind,
  { a: (c: Combatant, b: Balance) => number; b: (c: Combatant, b: Balance) => number }
> = Object.fromEntries(
  MATCHUP_KINDS.map((k) => [
    k,
    {
      a: (c: Combatant, b: Balance) => read(c, battleMap(b)[k].offense, b),
      b: (c: Combatant, b: Balance) => read(c, battleMap(b)[k].defense, b),
    },
  ]),
) as typeof STRENGTH;

/**
 * A stat as it plays right now: stages, paralysis, burn, fatigue down to the 0-HP floor, and
 * position fit applied.
 */
export function effectiveStat(c: Combatant, s: Exclude<StatName, 'hp'>, b: Balance): number {
  let v = c.stats[s] * stageMult(c.stages[s] ?? 0);
  if (c.status?.kind === 'paralysis' && s === 'spe') v *= b.status.paralysis.speedMult;
  if (c.status?.kind === 'burn' && s === 'atk') v *= b.status.burn.atkMult;
  v *= hpStatMult(c, b);
  return v * positionFit(c, b);
}

/**
 * Fatigue (slice 9b): 1 at or above `hp.fatigueThreshold` of max HP, then linear down to
 * `hp.zeroHpStatMult` at 0 HP, which stays the endpoint.
 */
export function hpStatMult(c: Pick<Combatant, 'hp' | 'stats'>, b: Balance): number {
  const { fatigueThreshold: t, zeroHpStatMult: floor } = b.hp;
  if (c.hp <= 0) return floor;
  const share = c.hp / c.stats.hp;
  return share >= t ? 1 : floor + ((1 - floor) * share) / t;
}

/**
 * Position fit (slice 9): 1 when the Pokémon lists no positions or lists the one it plays now;
 * otherwise balance.json `positionFit.mult` for the family of the position it plays.
 */
export function positionFit(c: Pick<Combatant, 'position' | 'positions'>, b: Balance): number {
  if (!c.positions || c.positions.includes(c.position)) return 1;
  return fitTable(b).get(c.position) ?? 1;
}

/** Position to its family's multiplier, built once per balance (the first family listing it). */
const FIT_TABLES = new WeakMap<Balance, Map<string, number>>();
function fitTable(b: Balance): Map<string, number> {
  let t = FIT_TABLES.get(b);
  if (!t) {
    t = new Map();
    const { families, mult } = b.positionFit;
    for (const [family, members] of Object.entries(families)) {
      for (const p of members as readonly string[]) {
        if (!t.has(p)) t.set(p, (mult as Record<string, number>)[family] ?? 1);
      }
    }
    FIT_TABLES.set(b, t);
  }
  return t;
}

/** The attack a move carries, or a clear error when a non-attack reaches the attack path. */
export function attackOf(move: Move): AttackEffect {
  if (move.effect.kind !== 'attack') throw new Error(`${move.id} is not an attack`);
  return move.effect;
}

/** 1 + power / powerDivisor for the formula balance.moves.attack.formula names. */
export function powerMult(power: number, b: Balance): number {
  const { formula, powerDivisor } = b.moves.attack;
  const div = (powerDivisor as Record<string, number | undefined>)[formula];
  if (div === undefined) throw new Error(`unknown attack formula "${formula}" in balance.json`);
  return 1 + power / div;
}

/** One side's numbers before mod and roll. */
interface Side {
  base: number;
  typeMult: number;
  stat?: BattleStat;
  attack?: AttackUse;
  /** The second defender's read averaged into `base` (slice 10q). */
  withBase?: number;
}

function attackSide(user: Combatant, opp: Combatant, move: Move, b: Balance): Side {
  const fx = attackOf(move);
  const stat: BattleStat = fx.category === 'physical' ? 'atk' : 'spa';
  const pm = powerMult(fx.power, b);
  const stab = user.types.includes(fx.type) ? b.moves.attack.stab : 1;
  return {
    base: effectiveStat(user, stat, b) * pm * stab,
    typeMult: Math.max(b.types.physicalFloor, effectiveness(fx.type, opp.types)),
    stat,
    attack: {
      moveId: move.id,
      type: fx.type,
      category: fx.category,
      hit: true,
      powerMult: pm,
      stab,
    },
  };
}

/** The side being hit by an attack it is not answering with one: its Def or Sp. Def. */
function defendSide(self: Combatant, attacker: Combatant, move: Move, b: Balance): Side {
  const stat: BattleStat = attackOf(move).category === 'physical' ? 'def' : 'spd';
  return {
    base: effectiveStat(self, stat, b),
    typeMult: physicalTypeMult(
      self.types,
      attacker.types,
      b.types.physicalFloor,
      b.types.battleExponent,
    ),
    stat,
  };
}

function missed(move: Move): AttackUse {
  const fx = attackOf(move);
  return {
    moveId: move.id,
    type: fx.type,
    category: fx.category,
    hit: false,
    powerMult: 1,
    stab: 1,
  };
}

/**
 * Resolve one 1-on-1 battle. One roll per side (option A), type multiplier on each side with the
 * physical floor, one crit check that replaces the result. Deterministic given the rng.
 *
 * A spent attack rolls its accuracy first (a, then b; no attack, no draw). If one side's attack
 * hits, that side is Atk or Sp. Atk x powerMult x STAB x the move type's effectiveness and the
 * other side is its Def or Sp. Def x its own type factor; if both hit, each side is its own attack.
 * A miss leaves that side to position strengths.
 */
export function resolveMatchup(input: MatchupInput, rng: Rng, b: Balance = balance): MatchupResult {
  const { kind, a, b: d } = input;
  const floor = b.types.physicalFloor;
  const exponent = b.types.battleExponent;
  const hits = (move: Move | undefined): boolean =>
    move !== undefined &&
    rng.next() < Math.max(b.moves.attack.accuracyFloor, attackOf(move).accuracy);
  const aHit = hits(input.aAttack);
  const bHit = hits(input.bAttack);

  let aSide: Side;
  let bSide: Side;
  if (aHit && input.aAttack) {
    aSide = attackSide(a, d, input.aAttack, b);
    bSide =
      bHit && input.bAttack
        ? attackSide(d, a, input.bAttack, b)
        : defendSide(d, a, input.aAttack, b);
  } else if (bHit && input.bAttack) {
    bSide = attackSide(d, a, input.bAttack, b);
    aSide = defendSide(a, d, input.bAttack, b);
  } else {
    const positional = kind === 'throw' || kind === 'kick';
    aSide = {
      base: STRENGTH[kind].a(a, b),
      typeMult: positional ? 1 : physicalTypeMult(a.types, d.types, floor, exponent),
    };
    // A second defender (slice 10q, the "averaged" gang form): his positional strength, read as he
    // plays, is averaged into b's base; the type multiplier stays b's own.
    const own = STRENGTH[kind].b(d, b);
    const withBase = input.bWith ? STRENGTH[kind].b(input.bWith, b) : undefined;
    bSide = {
      base: withBase === undefined ? own : (own + withBase) / 2,
      typeMult: positional ? 1 : physicalTypeMult(d.types, a.types, floor, exponent),
      ...(withBase === undefined ? {} : { withBase }),
    };
  }
  if (input.aAttack && !aHit) aSide.attack = missed(input.aAttack);
  if (input.bAttack && !bHit) bSide.attack = missed(input.bAttack);

  const aRoll = rng.roll(b.rolls.min, b.rolls.max);
  const bRoll = rng.roll(b.rolls.min, b.rolls.max);
  const aBase = aSide.base;
  const bBase = bSide.base;
  const aTypeMult = aSide.typeMult;
  const bTypeMult = bSide.typeMult;
  const aStrength = aBase * aTypeMult * (input.aMod ?? 1) * aRoll;
  const bStrength = bBase * bTypeMult * (input.bMod ?? 1) * bRoll;

  let winner: 'a' | 'b' = aStrength >= bStrength ? 'a' : 'b';
  let crit: 'a' | 'b' | undefined;
  // One crit check per matchup at the tuner rate; the crit goes to either side with equal chance
  // and that side wins outright, replacing the normal result.
  if (rng.chance(b.crits.rate)) {
    crit = rng.chance(0.5) ? 'a' : 'b';
    winner = crit;
  }

  const hi = Math.max(aStrength, bStrength);
  const lo = Math.max(1e-9, Math.min(aStrength, bStrength));
  const winnerMult = winner === 'a' ? aTypeMult : bTypeMult;
  // "It's super effective!" is for moves (PRD S4): only a winning attack that hit can be.
  const winnerHit = (winner === 'a' ? aSide : bSide).attack?.hit === true;

  return {
    kind,
    aBase,
    bBase,
    aStrength,
    bStrength,
    aTypeMult,
    bTypeMult,
    aRoll,
    bRoll,
    winner,
    margin: crit ? b.crits.marginOnCrit : hi / lo,
    ...(crit ? { crit } : {}),
    superEffective: winnerHit && winnerMult >= 2,
    ...(aSide.attack ? { aAttack: aSide.attack } : {}),
    ...(bSide.attack ? { bAttack: bSide.attack } : {}),
    ...(aSide.stat ? { aStat: aSide.stat } : {}),
    ...(bSide.stat ? { bStat: bSide.stat } : {}),
    ...(bSide.withBase !== undefined ? { bWithBase: bSide.withBase } : {}),
  };
}

/** HP the loser gives up for this result: the winner's margin scaled into the configured band. */
export function hpLossForLoss(result: MatchupResult, loserMaxHp: number, b: Balance): number {
  const { min, max, marginAtMax } = b.hp.lossPerLostBattle;
  const t = Math.min(1, (result.margin - 1) / (marginAtMax - 1));
  return Math.round(hpBasis(loserMaxHp, b) * (min + (max - min) * t));
}

/**
 * What hp's losses and heals are fractions of (slice 10h): the Pokémon's own max HP when
 * `hp.unit` is "fraction", the reference Pokémon's max HP when it is "points".
 */
export function hpBasis(maxHp: number, b: Balance): number {
  return b.hp.unit === 'points' ? referenceMaxHp(b) : maxHp;
}

/** Max HP of a Pokémon with base `hp.referenceBase` at `pvp.level` (stats.ts), once per balance. */
const REFERENCE_HP = new WeakMap<Balance, number>();
export function referenceMaxHp(b: Balance): number {
  let hp = REFERENCE_HP.get(b);
  if (hp === undefined) {
    const x = b.hp.referenceBase;
    hp = statsAtLevel({ hp: x, atk: x, def: x, spa: x, spd: x, spe: x }, b.pvp.level).hp;
    REFERENCE_HP.set(b, hp);
  }
  return hp;
}
